import { Href, useLocalSearchParams, useRouter } from 'expo-router';
import { AlertTriangleIcon } from 'lucide-react-native';
import { useEffect, useMemo, useState } from 'react';

import { SupportForm } from '@/components/supportForm';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Link, LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import { trackSmartLaunchFailed } from '@/util/analyticsEvents';
import { getSmartErrorCopy, isEmbeddedFrame, toSmartRefusalReason } from '@/util/smartLaunch';

/**
 * SMART EHR launch failure (/auth/smart/error?reason=…).
 *
 * Always offers ordinary sign-in as a way out. Inside the EHR's iframe that link opens a new
 * top-level tab, because Google refuses to run OAuth in a frame.
 */
export default function SmartErrorScreen() {
  const { replace } = useRouter();

  const params = useLocalSearchParams<{ reason?: string }>();

  const reason = toSmartRefusalReason(params.reason);

  const embedded = useMemo(isEmbeddedFrame, []);

  const [showSupportModal, setShowSupportModal] = useState(false);

  useEffect(() => {
    trackSmartLaunchFailed({ reason, embedded });
  }, [reason, embedded]);

  const openSignIn = () => {
    if (embedded) {
      window.open(ROUTES.LOGIN, '_blank', 'noopener');

      return;
    }

    replace(ROUTES.LOGIN as Href);
  };

  return (
    <VStack className="flex-1 items-center justify-center gap-6 p-6">
      <VStack className="w-full max-w-[420px] rounded-md bg-background-warning px-4 py-4">
        <HStack className="items-start gap-2">
          <Icon
            as={AlertTriangleIcon}
            className="mt-0.5 h-[18px] w-[18px] text-warning-800"
            aria-hidden
          />

          <VStack className="flex-1 gap-1.5">
            <Text size="sm" className="font-semibold text-typography-900" role="alert">
              We couldn’t sign you in from your EHR.
            </Text>

            <Text size="xs" className="text-typography-700">
              {getSmartErrorCopy(reason)}
            </Text>

            <HStack className="mt-2 items-center">
              <Link onPress={openSignIn}>
                <LinkText size="xs" className="text-warning-800 underline">
                  Sign in another way
                </LinkText>
              </Link>
              <Text size="xs" className="mx-1 text-typography-700">
                or
              </Text>
              <Link onPress={() => setShowSupportModal(true)}>
                <LinkText size="xs" className="text-warning-800 underline">
                  Contact Support
                </LinkText>
              </Link>
            </HStack>
          </VStack>
        </HStack>
      </VStack>

      {showSupportModal ? (
        <SupportForm isOpen={showSupportModal} onClose={() => setShowSupportModal(false)} />
      ) : null}
    </VStack>
  );
}
